import type { CustomFeesType, ProductFeeTermType } from '../types';
import get from 'lodash/get';
import omit from 'lodash/omit';
import { FEE_PAID_BY_LABELS } from './constants';

const getTermMax = (
  term: ProductFeeTermType,
  min: number,
  nextTerm: ProductFeeTermType | undefined
) => {
  if (term.max === -1 && !nextTerm) {
    return -1;
  }

  if (!nextTerm) {
    return term.max;
  }

  if (term.max === -1 && nextTerm) {
    if (term.min === 0) {
      return 0.99;
    }

    return nextTerm.min - 0.01;
  }

  const difference = nextTerm.min - term.max;

  if (difference === 1) {
    return term.max + 0.99;
  }

  if (difference === 0 && term.max > term.min) {
    return term.max - 0.01;
  }

  return Math.max(term.max, min + 1);
};

// massage overlapping terms
export const massageTermRanges = (
  rawTerms: Array<ProductFeeTermType> = []
): Array<ProductFeeTermType> => {
  const minMissing = rawTerms.some(({ min }) => min === undefined);
  const maxMissing = rawTerms.some(({ max }) => max === undefined);

  if (minMissing || maxMissing) {
    return rawTerms;
  }

  let counter = 0;

  return rawTerms.map((term, i) => {
    const min = Math.max(term.min, counter, 0);
    const max = getTermMax(term, min, rawTerms[i + 1]);
    counter = max;

    // .toFixed is required as 1.99 % 1 = 0.9900000000000002
    if ((counter % 1).toFixed(2) === '0.99') {
      counter += 0.01;
    }

    return {
      ...term,
      min,
      max
    };
  });
};

export const massageFeeAmounts = (
  _termRanges: CustomFeesType = []
): CustomFeesType => {
  const isRowAdded = _termRanges.some((r) => 'added' in r);
  const termRanges = _termRanges.map((r) => {
    if ('id' in r) {
      return Object.values(omit(r, ['id', 'added']));
    }

    return r;
  }) as CustomFeesType;
  const minMissing = termRanges.some((r) =>
    r.some(
      ({ contentType, content }) =>
        contentType === 'range' && content.min === undefined
    )
  );
  const maxMissing = termRanges.some((r) =>
    r.some(
      ({ contentType, content }) =>
        contentType === 'range' && content.max === undefined
    )
  );

  if (minMissing || maxMissing) {
    return termRanges;
  }

  if (termRanges.length === 0) return [];

  let lastMax = -0.01; // Initialize to allow the first minimum to be 0

  return termRanges.map((r, i) => {
    return r.map((term) => {
      if (term.contentType !== 'range') {
        return term;
      }

      const currentMin = term.content.min;
      const currentMax = term.content.max;
      const newMin = lastMax + 0.01;
      const newMax = Math.max(newMin + 0.01, currentMax);

      const nextMin = get(termRanges, `[${i + 1}][0].content.min`, 0);
      const isSecondLast = i === termRanges.length - 2;

      if (isSecondLast && isRowAdded) {
        const max = nextMin - 0.01;
        lastMax = max;

        return {
          ...term,
          content: {
            ...term.content,
            min: newMin,
            max
          }
        };
      }

      // Update lastMax for the next iteration
      lastMax = newMax;

      const isLast = i === termRanges.length - 1;

      return {
        ...term,
        content: {
          min: i === 0 ? 0 : newMin,
          max: currentMax === -1 && isLast ? -1 : newMax
        }
      };
    });
  });
};

export const getFeePaidByLabel = (_feePaidBy: string) => {
  const feePaidBy = _feePaidBy.toUpperCase();
  const label =
    feePaidBy in FEE_PAID_BY_LABELS ? FEE_PAID_BY_LABELS[feePaidBy] : '';

  if (!label) {
    console.error('Invalid feePaidBy value: ', feePaidBy);
  }

  return label || feePaidBy;
};
